// =========================================================
// ROUTE YOUR CAREER
// ITALY — BACHELOR'S + MASTER'S STUDY HUB
// =========================================================
//
// IMPORTANT:
// Italy should NOT be marketed as universally "zero tuition".
//
// Public-university tuition varies according to:
// - university
// - programme
// - family financial situation
// - ISEE / equivalent foreign-income documentation
// - scholarship / fee-waiver eligibility
//
// Some eligible students may receive major reductions,
// exemptions and regional right-to-study benefits.
//
// Always verify the final fee directly with the university
// for the relevant academic year.
// =========================================================

export const italyData = {

  // ======================================================
  // BASIC
  // ======================================================

  code: 'it',

  name: 'Italy',

  flag:
    'https://flagcdn.com/w80/it.png',


  // ======================================================
  // SEO
  // ======================================================

  seo: {

    title:
      'Study in Italy for Indian Students 2026 | Bachelor’s & Master’s',

    description:
      'Explore Bachelor’s and Master’s programmes in Italy for Indian students. Compare public universities, tuition fees, scholarships, eligibility, courses, visa process and living costs.',

    keywords: [
      'study in Italy for Indian students',
      'Bachelor in Italy',
      'Masters in Italy',
      'Italy public universities',
      'Italy scholarships for Indian students',
      'Italy university fees',
      'study abroad Italy',
      'English taught courses Italy',
      'Italy admission 2026'
    ]

  },


  // ======================================================
  // HERO
  // ======================================================

  tagline:
    'Bachelor’s + Master’s in Italy — world-class public universities at accessible fees.',

  intro:
    'Italy offers Indian students a wide range of English-taught Bachelor’s and Master’s programmes across business, economics, artificial intelligence, data science, engineering, architecture, law, biotechnology and other fields. Public-university tuition can be significantly lower than many other international destinations, with fee reductions and scholarships available to eligible students.',

  hero:
    'https://images.unsplash.com/photo-1499856871958-5b9627545d1a?w=1800&q=85',

  gallery: [

    'https://images.unsplash.com/photo-1516483638261-f4dbaf036963?w=1200&q=85',

    'https://images.unsplash.com/photo-1522093007474-d86e9bf7ba6f?w=1200&q=85',

    'https://images.unsplash.com/photo-1527142879-95b61a0b8226?w=1200&q=85',

  ],


  // ======================================================
  // QUICK FACTS
  // ======================================================

  quickFacts: [

    {
      k: 'Study levels',
      v: 'Bachelor + Master'
    },

    {
      k: 'Typical duration',
      v: 'UG 3 yr · PG 2 yr'
    },

    {
      k: 'Medium',
      v: 'English options'
    },

    {
      k: 'Public tuition',
      v: 'Varies by university'
    },

    {
      k: 'Main intake',
      v: '2026 / 27'
    },

    {
      k: 'Visa',
      v: 'Type D Study'
    },

  ],


  // ======================================================
  // PROGRAMME AREAS
  // ======================================================

  programmeAreas: [

    {
      name:
        'Business & Management',

      examples:
        'Business, Management, Economics, Finance, Marketing, International Business'
    },

    {
      name:
        'Computer Science & AI',

      examples:
        'Artificial Intelligence, Computer Science, Data Science, Robotics, Cybersecurity'
    },

    {
      name:
        'Engineering',

      examples:
        'Mechanical, Automotive, Computer, Electronics, Civil, Sustainable Engineering'
    },

    {
      name:
        'Economics & Finance',

      examples:
        'Economics, Finance, Business Economics, Economics & Management'
    },

    {
      name:
        'Architecture & Design',

      examples:
        'Architecture, Urban Planning, Sustainable Building, Design'
    },

    {
      name:
        'Law & International Studies',

      examples:
        'Global Law, European Studies, International Relations, Governance'
    },

    {
      name:
        'Life Sciences',

      examples:
        'Biotechnology, Biosciences, Health Economics and related fields'
    },

  ],


  // ======================================================
  // FEATURED BACHELOR'S PROGRAMMES
  // ======================================================

  featuredBachelors: [

    {
      university:
        'Sapienza University of Rome',

      course:
        'Applied Computer Science and Artificial Intelligence',

      field:
        'Computer Science & AI'
    },

    {
      university:
        'Sapienza University of Rome',

      course:
        'Sustainable Building Engineering',

      field:
        'Engineering'
    },

    {
      university:
        'University of Pavia',

      course:
        'Artificial Intelligence',

      field:
        'Artificial Intelligence'
    },

    {
      university:
        'University of Turin',

      course:
        'Business & Management',

      field:
        'Business'
    },

    {
      university:
        'University of Turin',

      course:
        'Global Law and Transnational Legal Studies',

      field:
        'Law'
    },

    {
      university:
        'University of Pisa',

      course:
        'Management for Business and Economics',

      field:
        'Management'
    },

    {
      university:
        'University of Rome Tor Vergata',

      course:
        'Business Administration & Economics',

      field:
        'Business & Economics'
    },

    {
      university:
        'Politecnico di Torino',

      course:
        'Computer Engineering',

      field:
        'Engineering'
    },

    {
      university:
        'Politecnico di Torino',

      course:
        'Automotive Engineering',

      field:
        'Engineering'
    },

    {
      university:
        'University of Camerino',

      course:
        'Biosciences and Biotechnology',

      field:
        'Life Sciences'
    },

  ],


  // ======================================================
  // FEATURED MASTER'S PROGRAMMES
  // ======================================================

  featuredMasters: [

    {
      university:
        'Sapienza University of Rome',

      course:
        'Artificial Intelligence and Robotics',

      field:
        'Artificial Intelligence'
    },

    {
      university:
        'Sapienza University of Rome',

      course:
        'Business Management',

      field:
        'Management'
    },

    {
      university:
        'Sapienza University of Rome',

      course:
        'Data Science',

      field:
        'Data Science'
    },

    {
      university:
        'Sapienza University of Rome',

      course:
        'Finance and Insurance',

      field:
        'Finance'
    },

    {
      university:
        'Sapienza University of Rome',

      course:
        'Management Engineering',

      field:
        'Engineering & Management'
    },

    {
      university:
        'Sapienza University of Rome',

      course:
        'Health Economics',

      field:
        'Economics'
    },

    {
      university:
        'Sapienza University of Rome',

      course:
        'Economics and Communication for Management and Innovation',

      field:
        'Economics & Management'
    },

    {
      university:
        'Sapienza University of Rome',

      course:
        'Electrical Engineering',

      field:
        'Engineering'
    },

  ],


  // ======================================================
  // ELIGIBILITY
  // ======================================================

  eligibility: [

    {
      k:
        'Bachelor’s',

      v:
        'Applicants generally need a recognised Class XII qualification. Subject prerequisites, minimum marks and entrance-test requirements depend on the individual programme.'
    },

    {
      k:
        'Master’s',

      v:
        'Applicants generally require a recognised Bachelor’s degree in a relevant academic field. Universities may assess previous subjects, credits and academic background.'
    },

    {
      k:
        'English',

      v:
        'English-taught programmes may require IELTS, TOEFL or another accepted proof of English. Some universities or programmes may accept alternative evidence, subject to their rules.'
    },

    {
      k:
        'Programme test',

      v:
        'Some Bachelor’s and Master’s programmes have entrance tests, interviews, portfolio requirements or additional selection criteria.'
    },

    {
      k:
        'Passport',

      v:
        'A valid passport is required for university pre-enrolment and the student visa process.'
    },

  ],


  // ======================================================
  // TUITION & LIVING COST
  // ======================================================

  fees: {

    tuition:
      'Public-university tuition varies by university, programme and financial eligibility.',

    tuitionNote:
      'Students may qualify for reduced tuition, partial exemptions or other benefits after submitting the required family-income and asset documentation. Do not assume that every public university or every student automatically pays zero tuition.',

    livingByCity: [

      {
        city: 'Milan',
        housing: 'Higher',
        food: 'Moderate',
        transport: 'Student rates',
        misc: 'Higher',
        total: '₹7–11L / yr approx.'
      },

      {
        city: 'Rome',
        housing: 'Med–High',
        food: 'Moderate',
        transport: 'Student rates',
        misc: 'Moderate',
        total: '₹6–10L / yr approx.'
      },

      {
        city: 'Bologna',
        housing: 'Med–High',
        food: 'Moderate',
        transport: 'Student rates',
        misc: 'Moderate',
        total: '₹6–9L / yr approx.'
      },

      {
        city: 'Padua',
        housing: 'Moderate',
        food: 'Moderate',
        transport: 'Student rates',
        misc: 'Moderate',
        total: '₹5–8L / yr approx.'
      },

      {
        city: 'Turin',
        housing: 'Moderate',
        food: 'Moderate',
        transport: 'Student rates',
        misc: 'Moderate',
        total: '₹5–8L / yr approx.'
      },

    ],

  },


  // ======================================================
  // SCHOLARSHIPS / FEE SUPPORT
  // ======================================================

  scholarships: [

    {
      name:
        'Regional Right-to-Study Benefits',

      body:
        'Italian regions operate financial-support schemes for eligible university students. Depending on the region and the student’s financial situation, support may include scholarships, accommodation, meals or other student benefits. Eligibility and award amounts vary each year.',

      tag:
        'NEED-BASED'
    },

    {
      name:
        'University Tuition Reductions',

      body:
        'Public universities may calculate tuition according to family financial circumstances or offer reduced fixed fees and exemptions to eligible international students. Rules vary by institution.',

      tag:
        'FEE REDUCTION'
    },

    {
      name:
        'University Merit Scholarships',

      body:
        'Individual universities may offer scholarships or tuition waivers based on academic merit, programme and yearly funding. Each scholarship has its own application requirements and deadlines.',

      tag:
        'MERIT'
    },

    {
      name:
        'MAECI Scholarships',

      body:
        'The Italian Ministry of Foreign Affairs and International Cooperation publishes scholarship calls for eligible international students, particularly for selected postgraduate and higher-education pathways. Students should check the current annual call.',

      tag:
        'GOVERNMENT'
    },

  ],


  // ======================================================
  // APPLICATION PROCESS
  // ======================================================

  visaSteps: [

    {
      m:
        'Step 1',

      title:
        'Course and university shortlist',

      body:
        'Choose programmes according to your previous education, academic subjects, career goal, tuition budget and admission requirements.'
    },

    {
      m:
        'Step 2',

      title:
        'University application',

      body:
        'Submit the university application with academic documents and any programme-specific requirements such as English scores, CV, SOP, portfolio or entrance tests.'
    },

    {
      m:
        'Step 3',

      title:
        'Admission decision',

      body:
        'If successful, the university issues the relevant admission or conditional admission documentation.'
    },

    {
      m:
        'Step 4',

      title:
        'Universitaly pre-enrolment',

      body:
        'Non-EU students requiring a study visa generally complete the applicable pre-enrolment procedure through the official Universitaly system.'
    },

    {
      m:
        'Step 5',

      title:
        'Student visa and arrival',

      body:
        'Complete the Type D study-visa process, travel to Italy and follow the required residence-permit and university-enrolment procedures after arrival.'
    },

  ],


  // ======================================================
  // UNIVERSITIES
  // ======================================================

  universities: [

    {
      name:
        'Sapienza University of Rome',

      short:
        'Sapienza',

      est:
        1303,

      city:
        'Rome',

      fee:
        'Public · fees vary',

      notes:
        'Large public research university offering English-taught options across artificial intelligence, data science, engineering, economics, management and other disciplines.',

      // SAME IMAGE SOURCE THAT WE KNOW LOADS
      img:
        'https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800&q=80'
    },


    {
      name:
        'University of Bologna',

      short:
        'UniBo',

      est:
        1088,

      city:
        'Bologna',

      fee:
        'Public · income-based options',

      notes:
        'Historic public university offering a broad portfolio of Bachelor’s and Master’s programmes. Tuition depends on the programme and financial circumstances.',

      img:
        'https://images.unsplash.com/photo-1562774053-701939374585?w=800&q=80'
    },


    {
      name:
        'Politecnico di Milano',

      short:
        'PoliMi',

      est:
        1863,

      city:
        'Milan',

      fee:
        'Public · fees vary',

      notes:
        'Major technical university with strong programmes in engineering, architecture, design and technology. Tuition and exemptions depend on student circumstances.',

      img:
        'https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800&q=80'
    },


    {
      name:
        'University of Padua',

      short:
        'UniPd',

      est:
        1222,

      city:
        'Padua',

      fee:
        'Public · fees vary',

      notes:
        'Established public research university offering programmes across science, engineering, economics, psychology and other academic fields.',

      img:
        'https://images.unsplash.com/photo-1568322445389-f64ac2515020?w=800&q=80'
    },


    {
      name:
        'University of Turin',

      short:
        'UniTo',

      est:
        1404,

      city:
        'Turin',

      fee:
        'Public · fees vary',

      notes:
        'Public university offering English-taught options including Business & Management, law and postgraduate programmes across multiple disciplines.',

      img:
        'https://images.unsplash.com/photo-1562774053-701939374585?w=800&q=80'
    },


    {
      name:
        'Politecnico di Torino',

      short:
        'PoliTo',

      est:
        1859,

      city:
        'Turin',

      fee:
        'Public · fees vary',

      notes:
        'Technical university with English-taught engineering and architecture options including Computer, Automotive and Electronic Engineering.',

      img:
        'https://images.unsplash.com/photo-1564981797816-1043664bf78d?w=800&q=80'
    },

  ],


  // ======================================================
  // DOCUMENTS
  // ======================================================

  documents: [

    'Valid passport',

    'Class X certificate and marksheet',

    'Class XII certificate and marksheet',

    'Bachelor’s degree documents for Master’s applicants',

    'Academic transcripts',

    'English-language proof where required',

    'Statement of Purpose / motivation letter when required',

    'CV / résumé for relevant programmes',

    'Letters of recommendation where required',

    'Portfolio for architecture / design programmes where applicable',

    'Financial documentation for tuition-benefit or scholarship applications',

    'Financial evidence required for the study visa',

    'Universitaly / pre-enrolment documentation where applicable',

    'Translated, legalised, apostilled or CIMEA / Declaration-of-Value documentation where required'

  ],


  // ======================================================
  // FAQ
  // ======================================================

  faqs: [

    {
      q:
        'Is university education in Italy free for Indian students?',

      a:
        'Not automatically. Public universities in Italy can be comparatively affordable, and eligible students may obtain fee reductions, exemptions or scholarships. The actual tuition depends on the university, programme and financial documentation.'
    },


    {
      q:
        'Can I study a Bachelor’s programme in English in Italy?',

      a:
        'Yes. English-taught Bachelor’s programmes are available in fields such as business, economics, artificial intelligence, engineering, law and other areas. The number of English options varies by university.'
    },


    {
      q:
        'Are Master’s programmes available in English?',

      a:
        'Yes. Italy offers a large selection of English-taught Master’s programmes across management, finance, data science, artificial intelligence, engineering, economics and many other disciplines.'
    },


    {
      q:
        'Do I need IELTS to study in Italy?',

      a:
        'It depends on the university and programme. Some programmes require IELTS, TOEFL or another recognised English qualification, while others may accept alternative evidence. Always check the exact programme requirement.'
    },


    {
      q:
        'What scholarships are available in Italy?',

      a:
        'Depending on eligibility, students may explore regional right-to-study benefits, university merit scholarships, tuition reductions and government scholarship programmes. Requirements and deadlines vary each year.'
    },


    {
      q:
        'How much does it cost to live in Italy?',

      a:
        'Living expenses vary significantly by city and accommodation type. Milan and Rome are generally more expensive, while several smaller university cities may offer lower student living costs.'
    },


    {
      q:
        'Which Italian university is best for me?',

      a:
        'The best option depends on your chosen course, previous academic background, budget, scholarship eligibility, city preference and career goal. Course-level matching is more useful than choosing only by university name.'
    },


    {
      q:
        'When should I start applying for the 2026/27 intake?',

      a:
        'Start researching programmes several months before deadlines because university applications, scholarship documents, Universitaly pre-enrolment and visa preparation can each have separate timelines.'
    },

  ],

};
